"use client";
import { Button } from "@/components/base/button";
import { Heading } from "@/components/base/heading";
import { InputCheckbox } from "@/components/base/input/input-checkbox";
import { InputErrorMessage } from "@/components/base/input/input-error-message";
import { FormStateContext } from "@/components/bli-medlem/form-state-context";
import { HorizontalContainer } from "@/components/layout/horizontal-container";
import { HorizontalSpacer } from "@/components/layout/horizontal-spacer";
import { PageContent } from "@/components/layout/page-content";
import { VerticalSpacer } from "@/components/layout/vertical-spacer";
import { VisuallyHidden } from "@/components/layout/visually-hidden";
import { ErrorMessage, ErrorMessageFlavour } from "@/components/ui/error-message";
import { Feedback } from "@/components/ui/feedback";
import { LoginLink } from "@/components/ui/log-in-or-register-links";
import { Text } from "@/components/ui/text";
import type { ErrorField } from "@/generated/client.generated";
import { isUserCreationErrorCode } from "@/utils/error-code-lookup";
import { paths } from "@/utils/paths-utils";
import type Joi from "joi";
import { useSession } from "next-auth/react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { type SyntheticEvent, useContext, useEffect, useState } from "react";
import { sendStepEvent } from "../analytics";
import { EMPTY_FORM_DATA, UserJourney } from "../types";
import { ContactInfoForm } from "./contact-info-form";
import { OtherPayer } from "./other-payer-form";
import { validateEmail, validateOtherUser, validateUser } from "./server-validation";
import { validateOtherUserPassword, validateUserPassword } from "./validation";

export const ContactInfoWrapper = () => {
  const { formData, setFormData, validateStep, validateStepField, currentStepIndex, step, next } =
    useContext(FormStateContext);
  const [validation, setValidation] = useState<Joi.ValidationErrorItem[]>([]);
  const [serverValidationErrors, setServerValidationErrors] = useState<ReadonlyArray<ErrorField>>([]);
  const [nextClicked, setNextClicked] = useState<boolean>(false);
  const { data: session } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Denne ønskes bare trigget av nextClicked
  useEffect(() => {
    if (nextClicked) {
      //include onBehalfOf user when validating the customerInfo step
      const clientValidation = validateStep({
        ...formData.contactInfo,
        onBehalfOf: { ...formData.onBehalfOf },
      });
      if (clientValidation.error) {
        setNextClicked(false);
        setValidation(clientValidation.error?.details ?? []);
        return;
      }
      setValidation([]);

      if (!validateUserPassword(formData.contactInfo, setValidation, formData.user.userId)) {
        setNextClicked(false);
        return;
      }

      if (!validateOtherUserPassword(formData.onBehalfOf, setValidation, formData.onBehalfOf?.otherPayer)) {
        setNextClicked(false);
        return;
      }

      setServerValidationErrors([]);
      validateUser(formData).then(async (serverValidation) => {
        if (serverValidation && serverValidation?.errors.length > 0) {
          setServerValidationErrors(serverValidation.errors);
          setNextClicked(false);
          return;
        }
        if (formData.onBehalfOf.otherPayer) {
          const otherServerValidation = await validateOtherUser(formData);
          if (otherServerValidation.errors.length > 0) {
            //Bytt navn på valideringsfeil slik at vi får vist ulik feil på de ulike feltene
            const modifiedServerValidationErrors = otherServerValidation.errors.map((error) => {
              return {
                ...error,
                field: `other${error.field.charAt(0).toUpperCase()}${error.field.slice(1)}`,
              };
            });
            setServerValidationErrors(modifiedServerValidationErrors);
            setNextClicked(false);
            return;
          }
        }

        sendStepEvent("", 4, step?.shortTitle, formData.userJourney);
        const path = paths.bliMedlemPrefilled(
          formData.userJourney,
          formData.customerType?.id,
          formData.mainCenter?.id,
          formData.accountType?.id,
          formData.accountType?.discountCode?.code,
          formData.contactInfo.email,
          undefined,
          formData.organization?.id,
        );

        if (`${pathname}?${searchParams.toString()}` === path) {
          next();
        } else {
          router.push(path);
        }

        setNextClicked(false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextClicked]);

  const validateOnBlur = (fieldValue: string, fieldName: string) => {
    const clientValidation = validateStepField(fieldValue, fieldName);
    setValidation(clientValidation.error?.details ?? []);
    return clientValidation?.error?.details;
  };

  const validateServerField = async (email: string) => {
    const response = await validateEmail(email);
    const fieldError = response?.errors.find((error) => error.field === "email");
    setServerValidationErrors(fieldError ? [fieldError] : []);
  };

  function handleToggleOtherPayer(checked: boolean) {
    if (formData.user.userId) {
      setFormData({
        onBehalfOf: {
          ...EMPTY_FORM_DATA.contactInfo,
          otherPayer: checked,
        },
        otherUser: { ...EMPTY_FORM_DATA.user },
      });
      return;
    }
    if (checked) {
      setFormData({
        contactInfo: {
          ...EMPTY_FORM_DATA.contactInfo,
        },
        onBehalfOf: {
          ...formData.contactInfo,
          otherPayer: true,
        },
        otherUser: formData.user,
        user: { ...EMPTY_FORM_DATA.user },
      });

      return;
    }

    setFormData({
      contactInfo: {
        ...formData.onBehalfOf,
      },
      onBehalfOf: {
        ...EMPTY_FORM_DATA.onBehalfOf,
        otherPayer: checked,
      },
      otherUser: { ...EMPTY_FORM_DATA.user },
      user: formData.otherUser,
    });
  }

  return (
    <PageContent width="small">
      <VisuallyHidden>
        <Heading type="h2">Skriv inn personalia i skjemaet</Heading>
      </VisuallyHidden>

      {/* hide annen betaler for bedrift memberships */}
      {formData.userJourney !== UserJourney.Organization && (
        <InputCheckbox
          id="input-other-payer"
          onChange={(e) => {
            // For logged in users, we keep the loggin in user as the payer
            handleToggleOtherPayer(e.target.checked);
          }}
          name="Annen betaler"
          label={"Skal du betale for noen andre?"}
          checked={formData.onBehalfOf.otherPayer}
        />
      )}

      <HorizontalSpacer size="medium" />
      {formData.onBehalfOf.otherPayer && (
        <>
          <HorizontalSpacer />
          <OtherPayer
            serverValidationErrors={serverValidationErrors}
            validation={validation}
            setValidation={setValidation}
            validateOnBlur={validateOnBlur}
          />
          <HorizontalSpacer size="large" />

          <Heading type="h2">Kort om betaler</Heading>

          {!session && (
            <>
              <HorizontalContainer verticalPlacement="center">
                <Text size="small" value="Allerede medlem?" />
                <VerticalSpacer size="x-small" />
                <LoginLink notItalic={true} />
              </HorizontalContainer>
              <HorizontalSpacer />
            </>
          )}
        </>
      )}

      <ContactInfoForm
        serverValidationErrors={serverValidationErrors}
        validation={validation}
        setValidation={setValidation}
        validateOnBlur={validateOnBlur}
        validateEmail={validateServerField}
      />

      <HorizontalSpacer size="medium" />

      <Button
        buttonType="primary"
        onClick={(e: SyntheticEvent) => {
          e.preventDefault();
          setFormData({
            // registrerer step i selectedValues med tom streng, for å få den frem i StepIndicator
            selectedValues: [
              ...formData.selectedValues,
              {
                name: step?.title ?? "",
                stepIndex: currentStepIndex,
                value: formData.contactInfo.email,
              },
            ],
          });
          setNextClicked(true);
        }}
        disabled={validation.length > 0}
      >
        Fortsett
      </Button>

      <HorizontalSpacer />

      <Feedback isError={true}>
        {serverValidationErrors.map((error) => {
          if (isUserCreationErrorCode(error.errorCode)) {
            return (
              <ErrorMessage
                key={error.errorCode}
                error={error.errorCode}
                errorMessageFlavour={ErrorMessageFlavour.CREATE_USER}
              />
            );
          }
          return (
            <InputErrorMessage
              key={error.errorCode}
              errorMessage={`Det skjedde en feil i dette feltet: ${error.field}`}
            />
          );
        })}
      </Feedback>
    </PageContent>
  );
};
